import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type { ContractModalSource } from '#src/app/pages/ambassadors/landing/pageContext';

import {
	AmbassadorsContractRequestSubmit,
	type AmbassadorsContractRequestSource,
	type AmbassadorsContractRequestSubmitMutation,
	type AmbassadorsContractRequestSubmitMutationVariables,
} from './index.graphql';

export type ContractFormValues = {
	name: string;
	email: string;
	handle: string;
	petitionIdea?: string;
	videoLink?: string;
	notSure: boolean;
};

// Mapped against the generated enum type rather than a `.toUpperCase()` cast.
const SOURCE_TO_GRAPHQL_ENUM: Readonly<Record<ContractModalSource, AmbassadorsContractRequestSource>> = {
	hero: 'HERO',
	footer: 'FOOTER',
	header: 'HEADER',
};

/**
 * Submits the contract request via the middle-end mutation, which forwards it to HubSpot.
 *
 * Rejects on both the `Failure` union member and a transport error, so the caller's `catch` shows
 * the error `Alert` either way.
 *
 * Don't add client-side error reporting here — the resolver owns it, and reporting from here risks
 * attaching `values` (name, email, handle) to a Sentry event.
 */
export async function submitContractRequest(
	{ gql: { fetch } }: UtilityContext,
	values: ContractFormValues,
	source: ContractModalSource | null,
): Promise<void> {
	const { data } = await fetch<
		AmbassadorsContractRequestSubmitMutation,
		AmbassadorsContractRequestSubmitMutationVariables
	>({
		path: '/ambassadors/contractRequestSubmit',
		query: AmbassadorsContractRequestSubmit,
		variables: {
			ambassadorsContractInput: {
				name: values.name,
				email: values.email,
				handle: values.handle,
				petitionIdea: values.petitionIdea,
				videoLink: values.videoLink,
				notSure: values.notSure,
				source: source === null ? null : SOURCE_TO_GRAPHQL_ENUM[source],
			},
		},
		// Keep this on its own HTTP request: batching would put several submissions in one POST, so
		// anything counting requests on this path — PerimeterX at the edge, the mutation's own
		// per-visitor/IP/global limiter in the middle-end — would see one.
		batched: false,
		rejectOnError: true,
	});

	if (data.ambassadorsContractRequestSubmit.__typename === 'AmbassadorsContractRequestSubmitFailure') {
		// Generic on purpose: never echo the server's `error` string or `values` into the message.
		throw new Error('Ambassadors contract request submission failed');
	}
}
